import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { focusEditable } from '../../DocEditor/utils/focusUtils';
import { sanitizeBlocksForPersist } from '../../DocEditor/utils/imageFiles';
import { splitTitleOnEnter } from '../titleEnter';
import { ADMIN_ONLY_TAG } from '../../../utils/docTags';
import { useAuth } from '../../AuthProvider';
import DocSkeleton from '../DocSkeleton';
import '../doc-skeleton.css';
import './DocFullPage.css';
import { useSnippetBacklinks } from '../../../hooks/useSnippetBacklinks';
import { useDocAutosave } from './hooks/useDocAutosave';
import { useDocLoad } from './hooks/useDocLoad';
import { useDocVariables } from './hooks/useDocVariables';
import DocFullPageTopbar from './components/DocFullPageTopbar';
import DocFullPageToolbar from './components/DocFullPageToolbar';
import DocFullPageContent from './components/DocFullPageContent';
import { useDocTabSheetScroll } from '../useDocTabSheetScroll';
import { DocScrollHost } from '../DocRestoreVeil';

const isMissingDocError = (message) =>
  /404|not found|deleted/i.test(message || '');

/**
 * DocFullPage — the master document view for a single Doc, opened from the
 * library. This is "editing mode": the clean master copy. ("Doing mode" —
 * opening a linked Doc from inside a mission — is a peek overlay, Chunk 10a.)
 *
 * The list only carries metadata, so the body is fetched here on open.
 * Title, icon and body all edit in place and autosave (debounced, flushed
 * on Back/unmount so nothing is lost by leaving quickly).
 *
 * DOC-LEVEL PERMISSION (Mission Docs Become Real Docs): the `admin-only`
 * reserved tag hides a doc from user-tier members everywhere (library,
 * pickers, mission attachments). Admins toggle it here.
 */
const DocFullPage = ({
  docId,
  onBack,
  /** What Back returns to — previous doc title, or the library */
  backLabel = 'Docs',
  /** Always closes the whole sheet (distinct from nested Back) */
  onClose,
  /** 'sheet' = fill DocSheet panel with fixed chrome + internal scroll */
  layout = 'page',
  onSave,
  libraryDocs = [],
  onOpenDoc,
  /** Optional useDocOrganization result — library context only (missions don't pass it) */
  organization = null,
  /** Close the tab that pointed at a missing doc */
  onDismiss = null,
}) => {
  const { role } = useAuth();
  const isAdmin = (role ?? 'admin') !== 'user';
  const inSheet = layout === 'sheet';
  const scrollRef = useRef(null);
  const holdCover = useDocTabSheetScroll(inSheet ? docId : null, scrollRef);

  const {
    doc,
    setDoc,
    blocks,
    setBlocks,
    title,
    setTitle,
    loadError,
    docVariables,
    setDocVariables,
    variablesColumnWidths,
    setVariablesColumnWidths,
  } = useDocLoad(docId);

  const { saveState, scheduleSave, saveNow, flush } = useDocAutosave(docId, onSave);
  const { backlinksByBlockId } = useSnippetBacklinks(docId);

  const {
    scopedVariables,
    resolveVariable,
    handleDocVariablesChange,
    handleVariablesColumnWidthsChange,
    handleCreateVariable,
  } = useDocVariables({
    docVariables,
    setDocVariables,
    setVariablesColumnWidths,
    saveNow,
  });

  // Page layout has no tab-owned scroll memory — pin to top on nav.
  useLayoutEffect(() => {
    if (inSheet) return;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [docId, inSheet]);

  const docsById = useMemo(() => {
    const map = new Map();
    for (const d of libraryDocs) map.set(d.id, d);
    return map;
  }, [libraryDocs]);
  const resolveDocMeta = useCallback((id) => docsById.get(id), [docsById]);

  const handleBack = useCallback(async () => {
    await flush();
    onBack();
  }, [flush, onBack]);

  const handleBlocksChange = useCallback(
    (tree) => {
      setBlocks(tree);
      scheduleSave({ body: sanitizeBlocksForPersist(tree) });
    },
    [scheduleSave, setBlocks]
  );

  const handleTitleChange = useCallback(
    (e) => {
      setTitle(e.target.value);
      scheduleSave({ title: e.target.value });
    },
    [scheduleSave, setTitle]
  );

  const handleTitleKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const input = e.target;
      const next = splitTitleOnEnter(
        title,
        input.selectionStart,
        input.selectionEnd,
        blocks
      );
      setTitle(next.title);
      setBlocks(next.blocks);
      scheduleSave({
        title: next.title,
        body: sanitizeBlocksForPersist(next.blocks),
      });
      focusEditable(next.focusBlockId, { atStart: true });
    },
    [title, blocks, scheduleSave, setTitle, setBlocks]
  );

  const handleIconChange = useCallback(
    (newIcon) => {
      setDoc((prev) => (prev ? { ...prev, icon: newIcon } : prev));
      scheduleSave({ icon: newIcon });
    },
    [scheduleSave, setDoc]
  );

  const handleToggleAdminOnly = useCallback(() => {
    setDoc((prev) => {
      if (!prev) return prev;
      const tags = Array.isArray(prev.tags) ? prev.tags : [];
      const nextTags = tags.includes(ADMIN_ONLY_TAG)
        ? tags.filter((t) => t !== ADMIN_ONLY_TAG)
        : [...tags, ADMIN_ONLY_TAG];
      scheduleSave({ tags: nextTags });
      return { ...prev, tags: nextTags };
    });
  }, [scheduleSave, setDoc]);

  const rootClass = inSheet ? 'doc-fullpage doc-fullpage--sheet' : 'doc-fullpage';
  const isGone = isMissingDocError(loadError);
  const closeMissing = onDismiss || onClose;
  const docReady = Boolean(doc?.id === docId);

  // Always mount the toolbar strip — DocEditor portals into the slot.
  // Hiding it while `doc` is null made link/back navigation blink the chrome.
  const topbar = (
    <DocFullPageTopbar
      backLabel={backLabel}
      onBack={handleBack}
      onClose={isGone ? closeMissing : onClose}
      doc={isGone || !docReady ? null : doc}
      organization={organization}
      docId={docId}
      isAdmin={isAdmin}
      onToggleAdminOnly={handleToggleAdminOnly}
    />
  );
  const toolbar = <DocFullPageToolbar saveState={saveState} />;

  const scrollPort = (children) => (
    <DocScrollHost className="doc-fullpage__scroll-host" holdCover={holdCover}>
      <div className="doc-fullpage__scroll" ref={scrollRef}>{children}</div>
    </DocScrollHost>
  );

  if (isGone) {
    return (
      <div className={rootClass}>
        <div className="doc-fullpage__chrome">
          {topbar}
        </div>
        {scrollPort(
          <div className="doc-fullpage__missing">
            <p className="doc-fullpage__missing-title">This doc no longer exists</p>
            <p className="doc-fullpage__missing-text">
              It may have been deleted. You can close this tab.
            </p>
            {onDismiss && (
              <button
                type="button"
                className="doc-fullpage__missing-close"
                onClick={onDismiss}
              >
                Close tab
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={rootClass}>
        <div className="doc-fullpage__chrome">
          {topbar}
          {toolbar}
        </div>
        {scrollPort(
          <div className="doc-fullpage__load-state">{loadError}</div>
        )}
      </div>
    );
  }

  if (!docReady) {
    return (
      <div className={rootClass}>
        <div className="doc-fullpage__chrome">
          {topbar}
          {toolbar}
        </div>
        {scrollPort(
          holdCover
            ? null
            : <DocSkeleton meta={docsById.get(docId)} variant="fullpage" />
        )}
      </div>
    );
  }

  const docContent = (
    <DocFullPageContent
      docId={docId}
      doc={doc}
      title={title}
      blocks={blocks}
      onTitleChange={handleTitleChange}
      onTitleKeyDown={handleTitleKeyDown}
      onIconChange={handleIconChange}
      onBlocksChange={handleBlocksChange}
      onOpenDoc={onOpenDoc}
      resolveDocMeta={resolveDocMeta}
      libraryDocs={libraryDocs}
      snippetBacklinks={backlinksByBlockId}
      resolveVariable={resolveVariable}
      scopedVariables={scopedVariables}
      onCreateVariable={handleCreateVariable}
      docVariables={docVariables}
      onDocVariablesChange={handleDocVariablesChange}
      variablesColumnWidths={variablesColumnWidths}
      onVariablesColumnWidthsChange={handleVariablesColumnWidthsChange}
    />
  );

  if (inSheet) {
    return (
      <div className={rootClass}>
        <div className="doc-fullpage__chrome">
          {topbar}
          {toolbar}
        </div>
        {scrollPort(docContent)}
      </div>
    );
  }

  return (
    <div className={rootClass}>
      {topbar}
      {toolbar}
      {docContent}
    </div>
  );
};

export default DocFullPage;
